import React, { useState, useCallback, useRef, useEffect } from "react";
import { Box, VStack, Text, Input, Button } from "@chakra-ui/react";
import { FiArrowRight } from "react-icons/fi";
import { useSpring, animated, config } from "react-spring";
import { useNavigate, useParams } from "react-router-dom";
import { useCpf } from "@/contexts/cpfContext";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { useConditionalBackground } from "@/contexts/useConditionalBackground ";
import Logo from "@/components/Logo";

const AnimatedText = () => {
  const [index, setIndex] = useState(0);
  const [key, setKey] = useState(0);
  const phrases = [
    "confiável",
    "prático",
    "eficiente",
    "acessível",
    "seguro",
    "inovador",
    "digital",
    "simples",
    "rápido",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % phrases.length);
      setKey((prevKey) => prevKey + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const { transform, opacity } = useSpring({
    opacity: 1,
    transform: `perspective(600px) rotateX(0deg)`,
    from: { opacity: 0, transform: `perspective(600px) rotateX(-90deg)` },
    reset: true,
    config: { mass: 5, tension: 150, friction: 30 },
  });

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="center"
      mb={{ base: 4, sm: 5, md: 6, lg: 8 }}
    >
      <Text
        fontSize={{ base: "7xl", sm: "8xl", md: "9xl", lg: "9xl" }}
        fontWeight="black"
        color="white"
        lineHeight="0.9"
        mb={2}
      >
        Mais
      </Text>
      <Box
        position="relative"
        height={{ base: "70px", sm: "80px", md: "90px", lg: "100px" }}
        width="100%"
        ml={12}
      >
        <animated.div
          key={key}
          style={{
            position: "absolute",
            opacity,
            transform,
            transformStyle: "preserve-3d",
          }}
        >
          <Text
            fontSize={{ base: "6xl", sm: "7xl", md: "8xl", lg: "9xl" }}
            fontWeight="black"
            color="white"
            lineHeight="0.9"
            whiteSpace="nowrap"
          >
            {phrases[index]}
          </Text>
        </animated.div>
      </Box>
    </Box>
  );
};

export default function OnBoarding() {
  const [showButton, setShowButton] = useState(false);
  const { cpf, setCpf } = useCpf();
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { shorthash } = useParams<{ shorthash: string }>();
  const backgroundStyle = useConditionalBackground();

  const handleCpfChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const maskedValue = cpfFormatter(e.target.value);
      setCpf(maskedValue);
      if (maskedValue.length === 14 && !showButton) {
        setShowButton(true);
        inputRef.current?.blur();
      }
    },
    [showButton]
  );

  useEffect(() => {
    if (cpf.length === 14) {
      setShowButton(true);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (cpf.length === 14) {
      navigate(`/${shorthash}/introducao`);
    }
  }, [cpf]);

  const buttonAnimation = useSpring({
    opacity: showButton ? 1 : 0,
    transform: showButton ? "translateY(0px)" : "translateY(20px)",
    config: config.gentle,
  });

  return (
    <Box
      height="100vh"
      w="100vw"
      {...backgroundStyle}
      px={{ base: 4, sm: 6, md: 8, lg: 12 }}
      py={{ base: 2, sm: 3, md: 4, lg: 6 }}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      overflowY="hidden"
    >
      <Box mt={4} ml={2} flexShrink={0}>
        <Logo variant="primary" alt="Logo"
          h={{ base: "5rem", sm: "6rem", md: "7rem", lg: "8rem" }}
          mb={{ base: 0, sm: 1, md: 2, lg: 3 }}
        />
      </Box>

      <VStack
        align="stretch"
        spacing={{ base: 6, sm: 5, md: 6, lg: 8 }}
        height="auto"
        justify="center"
        width="100%"
      >
        <AnimatedText />
        <Box width="100%" mx="auto">
          <Input
            ref={inputRef}
            placeholder="Digite seu CPF"
            type="tel"
            inputMode="numeric"
            pattern="\d*"
            size="lg"
            height={{ base: "4rem", sm: "4.5rem", md: "5rem", lg: "5.5rem" }}
            fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
            textAlign="center"
            bg="transparent"
            color="white"
            border="none"
            borderBottom="2px solid"
            borderColor="whiteAlpha.500"
            _placeholder={{ color: "whiteAlpha.700" }}
            _focus={{
              borderColor: "white",
              boxShadow: "none",
            }}
            value={cpf}
            onChange={handleCpfChange}
            mb={4}
          />
          <Text
            fontSize={{ base: "sm", sm: "md", md: "lg", lg: "xl" }}
            color="whiteAlpha.800"
            mb={8}
            textAlign="center"
          >
            Seu CPF é necessário para iniciarmos seu cadastro de forma segura {cpf}
          </Text>
          <animated.div style={buttonAnimation}>
            <Button
              bg="white"
              color="primary"
              size="lg"
              height={{
                base: "4rem",
                sm: "4.5rem",
                md: "5rem",
                lg: "5.5rem",
              }}
              fontSize={{ base: "xl", sm: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              onClick={handleSubmit}
              isDisabled={cpf.length !== 14}
              _hover={{
                bg: "whiteAlpha.800",
              }}
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <Text mr={4}>Vamos Lá</Text>
              <Box position="absolute" right="5%" height="50%">
                <FiArrowRight style={{ height: "100%", width: "auto" }} />
              </Box>
            </Button>
          </animated.div>
        </Box>
      </VStack>

      <Text
        fontSize={{ base: "xs", sm: "sm", md: "md", lg: "lg" }}
        color="whiteAlpha.600"
        textAlign="center"
        flexShrink={0}
        mt={{ base: 1, sm: 2, md: 3, lg: 4 }}
      >
        © 2023 GranaTech. Todos os direitos reservados.
      </Text>
    </Box>
  );
}
